import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';

/**
 * Ownership ref applied to Microsoft (Entra) users by the
 * `auth-backend-module-microsoft-provider` sign-in resolver. External users get
 * a significantly reduced UI (Vibe Coded Apps + Blueprints only).
 */
export const EXTERNAL_GROUP_REF = 'group:default/external';

/**
 * Routes external (Microsoft) users are allowed to navigate to: Vibe Coded Apps,
 * Blueprints, Settings (so they can manage their profile / sign out), and the
 * OAuth consent page (so they can authorize MCP clients).
 */
export const EXTERNAL_ALLOWED_ROUTES = [
  '/cloudflare-workers',
  '/vibe-coded-apps',
  '/create',
  '/settings',
  '/oauth2/authorize',
];

/** Where external users are sent when they hit a disallowed route. */
export const EXTERNAL_HOME_ROUTE = '/cloudflare-workers';

export type ExternalUserState = {
  /** True until the identity has been resolved. */
  loading: boolean;
  /** True when the signed-in user is an external (Microsoft) user. */
  isExternal: boolean;
};

/**
 * Resolves whether the signed-in user is an external (Microsoft) user, based on
 * the `group:default/external` ownership ref in their Backstage identity.
 */
export function useIsExternalUser(): ExternalUserState {
  const identityApi = useApi(identityApiRef);
  const [state, setState] = useState<ExternalUserState>({
    loading: true,
    isExternal: false,
  });

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!active) {
          return;
        }
        setState({
          loading: false,
          isExternal: identity.ownershipEntityRefs.includes(EXTERNAL_GROUP_REF),
        });
      })
      .catch(() => {
        if (active) {
          setState({ loading: false, isExternal: false });
        }
      });
    return () => {
      active = false;
    };
  }, [identityApi]);

  return state;
}

/**
 * Returns true when `pathname` is within the set of routes external users may
 * access.
 */
export function isExternalAllowedPath(pathname: string): boolean {
  return EXTERNAL_ALLOWED_ROUTES.some(
    allowed => pathname === allowed || pathname.startsWith(`${allowed}/`),
  );
}
